import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Subject } from 'rxjs';
import { observableToPromise } from '../functions/utility';

@Injectable({
  providedIn: 'root'
})
export class Auth0Service {
  patchProfileState$: Subject<{ state: string, payload: any }> = new Subject<{ state: string, payload: any }>();
  requestPasswordChangeState$: Subject<{ state: string, payload: any }> = new Subject<{ state: string, payload: any }>();

  constructor(
    private httpClient: HttpClient
  ) { }

  async patchProfile(authUserId: string, data: { email?: string, family_name?: string, given_name?: string }): Promise<any> {
    try {

      this.patchProfileState$.next({ state: 'patch-profile-initialize', payload: null });

      const postData = {
        data: {
          type: 'Service',
          attributes: data
        }
      };

      const userUpdateUrl = environment.openbridgeApiUris.service + '/service/auth0/user/'
        + encodeURIComponent(authUserId);

      const response = await observableToPromise(this.httpClient.post(userUpdateUrl, postData));

      this.patchProfileState$.next({ state: 'patch-profile-complete', payload: null });
      return response;

    }
    catch (error) {
      this.patchProfileState$.next({ state: 'patch-profile-complete', payload: null });
      throw error;
    }
  }

  async requestPasswordChange(email: string): Promise<any> {
    try {
      this.requestPasswordChangeState$.next({ state: 'password-change-request-initialize', payload: null });

      const headers = new HttpHeaders().set('Content-Type', 'application/json');
      const options = { headers, responseType: 'text' as const };

      const payload = {
        connection: 'Username-Password-Authentication',
        client_id: environment.auth0.clientId,
        email: email
      };

      const response = await observableToPromise(this.httpClient.post(`https://${environment.auth0.domain}/dbconnections/change_password`, payload, options));

      this.patchProfileState$.next({ state: 'password-change-request-complete', payload: null });

      return response;
    }
    catch (error) {
      this.patchProfileState$.next({ state: 'password-change-request-complete', payload: null });
      throw error;
    }
  }

  async createOrganization(orgName: string): Promise<any> {
    try {

      const payload = {};

      this.patchProfileState$.next({ state: 'patch-profile-initialize', payload: null });

      const requestUrl = environment.openbridgeApiUris.service + '/service/org';

      const response = await observableToPromise(this.httpClient.post(requestUrl, payload));

      this.patchProfileState$.next({ state: 'patch-profile-complete', payload: null });
      return response;

    }
    catch (error) {
      this.patchProfileState$.next({ state: 'patch-profile-complete', payload: null });
      throw error;
    }
  }

  async getOrganization(): Promise<any> {
    try {

      this.patchProfileState$.next({ state: 'patch-profile-initialize', payload: null });

      const requestUrl = environment.openbridgeApiUris.service + '/service/org';
      const response = await observableToPromise(this.httpClient.get(requestUrl));
      this.patchProfileState$.next({ state: 'patch-profile-complete', payload: null });
      return response;

    }
    catch (error) {
      this.patchProfileState$.next({ state: 'patch-profile-complete', payload: null });
      throw error;
    }

  }

  async getOrganizationByName(orgName: string): Promise<any> {
    try {

      this.patchProfileState$.next({ state: 'patch-profile-initialize', payload: null });

      const response = await observableToPromise(this.httpClient.get(`${environment.openbridgeApiUris.service}/service/org/name/${orgName}`));

      this.patchProfileState$.next({ state: 'patch-profile-complete', payload: null });
      return response;

    }
    catch (error) {
      this.patchProfileState$.next({ state: 'patch-profile-complete', payload: null });
      throw error;
    }
  }

  async deleteOrganization(): Promise<any> {
    try {

      this.patchProfileState$.next({ state: 'patch-profile-initialize', payload: null });

      const requestUrl = environment.openbridgeApiUris.service + '/service/org';
      const response = await observableToPromise(this.httpClient.delete(requestUrl));
      this.patchProfileState$.next({ state: 'patch-profile-complete', payload: null });
      return response;

    }
    catch (error) {
      this.patchProfileState$.next({ state: 'patch-profile-complete', payload: null });
      throw error;
    }
  }

  async createOrganizationInvitation(): Promise<any> {
    try {

      const payload = {};

      this.patchProfileState$.next({ state: 'patch-profile-initialize', payload: null });

      const requestUrl = environment.openbridgeApiUris.service + '/service/org/invites';
      const response = await observableToPromise(this.httpClient.post(requestUrl, payload));

      this.patchProfileState$.next({ state: 'patch-profile-complete', payload: null });
      return response;

    }
    catch (error) {
      this.patchProfileState$.next({ state: 'patch-profile-complete', payload: null });
      throw error;
    }
  }

  async getOrganizationInvitation(): Promise<any> {
    try {

      this.patchProfileState$.next({ state: 'patch-profile-initialize', payload: null });
      const response = await observableToPromise(this.httpClient.get(`${environment.openbridgeApiUris.service}/service/org/invites`));
      this.patchProfileState$.next({ state: 'patch-profile-complete', payload: null });
      return response;

    }
    catch (error) {
      this.patchProfileState$.next({ state: 'patch-profile-complete', payload: null });
      throw error;
    }
  }

  async deleteOrganizationInvitation(): Promise<any> {
    try {

      this.patchProfileState$.next({ state: 'patch-profile-initialize', payload: null });

      const response = await observableToPromise(this.httpClient.delete(`${environment.openbridgeApiUris.service}/service/org/invites`));

      this.patchProfileState$.next({ state: 'patch-profile-complete', payload: null });
      return response;

    }
    catch (error) {
      this.patchProfileState$.next({ state: 'patch-profile-complete', payload: null });
      throw error;
    }
  }

  async createOrganizationMembers(): Promise<any> {
    try {

      const payload = {};

      this.patchProfileState$.next({ state: 'patch-profile-initialize', payload: null });

      const response = await observableToPromise(this.httpClient.post(`${environment.openbridgeApiUris.service}/service/org/members`, payload));

      this.patchProfileState$.next({ state: 'patch-profile-complete', payload: null });
      return response;

    }
    catch (error) {
      this.patchProfileState$.next({ state: 'patch-profile-complete', payload: null });
      throw error;
    }
  }

  async getOrganizationMembers(): Promise<any> {
    try {

      this.patchProfileState$.next({ state: 'patch-profile-initialize', payload: null });

      const response = await observableToPromise(this.httpClient.get(`${environment.openbridgeApiUris.service}/service/org/members`));
      this.patchProfileState$.next({ state: 'patch-profile-complete', payload: null });
      return response;

    }
    catch (error) {
      this.patchProfileState$.next({ state: 'patch-profile-complete', payload: null });
      throw error;
    }

  }

  async deleteOrganizationMembers(members: string[]): Promise<any> {
    try {

      const payload = {};

      this.patchProfileState$.next({ state: 'patch-profile-initialize', payload: null });
      const response = await observableToPromise(this.httpClient.delete(`${environment.openbridgeApiUris.service}/service/org/members`, { body: payload }));

      this.patchProfileState$.next({ state: 'patch-profile-complete', payload: null });
      return response;

    }
    catch (error) {
      this.patchProfileState$.next({ state: 'patch-profile-complete', payload: null });
      throw error;
    }

  }

  async resendVerificationEmail(auth0UserId: string): Promise<any> {
    const payload = {};
    return await observableToPromise(this.httpClient.post(`${environment.openbridgeApiUris.service}/service/auth0/user/verify-email/${auth0UserId}`, payload));
  }

}